/**
 * ContentPage — Phase 1.5 magazine-flow orchestrator.
 *
 * Lead Phase 1.5 brief: a single vertical scrolling document. No nested scroll
 * boxes, no left/right sidebar columns, no persistent platform chrome (that's
 * hidden by HUDLayout's immersive branch). Picture Show is the cover; On This
 * Day surfaces alongside in the same era-moment; script flows below; lineage
 * intersections inline at era match; sticky step nav lets the reader jump
 * steps without losing scroll context.
 *
 * The colocated helpers (PinholeTransition, PictureShowHero, OnThisDayInline,
 * MyRootsInlineCallout, ContentWarning, StickyStepNav) live in /content/ to
 * keep this file focused on data wiring + assembly.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, BookOpen, ChevronLeft, ChevronRight, Clock,
  MapPin, Pause, Pen, Play, Sparkles, Volume2,
} from 'lucide-react';

import { creditsAPI, lessonAPI, lineageAPI, mediaAPI } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { eraToCSS, getEraForYear } from '../lib/eraGrammar';

import EraScript from './EraScript';
import StepRibbon from './StepRibbon';
import PinholeTransition from './content/PinholeTransition';
import PictureShowHero from './content/PictureShowHero';
import OnThisDayInline from './content/OnThisDayInline';
import MyRootsInlineCallout from './content/MyRootsInlineCallout';
import StickyStepNav from './content/StickyStepNav';
import InspiredBy from './content/InspiredBy';
import DraftBadge from './DraftBadge';
import ContentWarning from './content/ContentWarning';
import CrossReferenceBlock from './content/CrossReferenceBlock';
import ForkChainButton from './ForkChainButton';
import { resolveEraOrigin } from '../lib/eraOrigins';

const STEPS = [
  { key: 'origin', label: 'Origin', sublabel: 'The Root' },
  { key: 'change_a', label: 'Change A', sublabel: 'First Modification' },
  { key: 'change_b', label: 'Change B', sublabel: 'Second Modification' },
  { key: 'contradiction', label: 'Contradiction', sublabel: 'The Split' },
  { key: 'modern_pattern', label: 'Modern Pattern', sublabel: 'The Consistency' },
];
const STEP_KEYS = STEPS.map((s) => s.key);

const EMPTY_CONTRADICTION =
  'No major conflicting verified accounts are currently attached to this record. If you have a source that challenges this record, you can submit it for review.';

export default function ContentPage({ chain, onNavigate, onViewChange }) {
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPinhole, setShowPinhole] = useState(true);
  const [warningAccepted, setWarningAccepted] = useState(false);
  const [chainMedia, setChainMedia] = useState([]);
  const [intersections, setIntersections] = useState([]);
  const [credits, setCredits] = useState({ current_balance: 0, lifetime_earned: 0 });
  const [neighbors, setNeighbors] = useState({ previous: null, next: null });
  const audioRef = useRef(null);
  const scrollRef = useRef(null);
  const scriptRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();

  useEffect(() => {
    if (!chain?.id) return;
    setLoading(true);
    setShowPinhole(true);
    setCurrentStep(0);
    lessonAPI.get(chain.id)
      .then((r) => { setLesson(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    mediaAPI.listChain(chain.id).then((r) => setChainMedia(r.data || [])).catch(() => setChainMedia([]));
    lineageAPI.intersections(chain.id).then((r) => setIntersections(r.data || [])).catch(() => setIntersections([]));
    creditsAPI.get().then((r) => setCredits(r.data)).catch(() => {});
    api.get(`/chains/${chain.id}/neighbors`).then((r) => setNeighbors(r.data || { previous: null, next: null })).catch(() => setNeighbors({ previous: null, next: null }));
  }, [chain?.id]);

  const step = STEPS[currentStep];
  const stepData = lesson?.chain?.[step?.key];
  const isContradiction = step?.key === 'contradiction';

  const stepImages = chainMedia
    .filter((m) => m.media_type === 'image' && m.step_key === step?.key)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map((m) => ({
      url: mediaAPI.fileUrl(m.id),
      caption: m.caption || stepData?.title,
      source: m.source || stepData?.source,
    }));

  const narrationFile =
    chainMedia.find((m) => m.media_type === 'audio' && m.is_narration && m.step_key === step?.key) ||
    chainMedia.find((m) => m.media_type === 'audio' && m.is_narration && !m.step_key);
  const narrationUrl = narrationFile ? mediaAPI.fileUrl(narrationFile.id) : null;

  const stepIntersections = (intersections || []).filter((it) => it.step_key === step?.key);

  // Sync playback with audio element
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !narrationUrl) return;
    if (isPlaying) audio.play().catch(() => setIsPlaying(false));
    else audio.pause();
  }, [isPlaying, narrationUrl]);

  // Reset audio on step change
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.currentTime = 0;
  }, [currentStep, narrationUrl]);

  // Scroll script section into view on step change (after pinhole done)
  useEffect(() => {
    if (showPinhole) return;
    if (scriptRef.current && scrollRef.current) {
      const top = scriptRef.current.offsetTop - 80;
      scrollRef.current.scrollTo({ top, behavior: 'smooth' });
    }
  }, [currentStep, showPinhole]);

  const handlePinholeComplete = useCallback(() => setShowPinhole(false), []);
  const goStep = (i) => setCurrentStep(Math.max(0, Math.min(STEPS.length - 1, i)));

  if (lesson && !warningAccepted && lesson.content_warnings?.length > 0) {
    return <ContentWarning warnings={lesson.content_warnings} onProceed={() => setWarningAccepted(true)} />;
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-full">
        <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse">LOADING LESSON...</div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="p-6 text-center">
        <p className="font-mono text-sm text-[#52525B]">Lesson not found.</p>
      </div>
    );
  }

  const eraYear = stepData?.year || lesson?.chain?.origin?.year || 2026;
  const era = getEraForYear(eraYear);
  const eraStyles = eraToCSS(era);
  const frameClass = `era-frame-${era.frameStyle}`;

  return (
    <div className="h-full era-content" style={eraStyles} data-testid="content-page" data-era={era.id}>
      <PinholeTransition
        active={showPinhole}
        onComplete={handlePinholeComplete}
        imageUrl={chain?.pinhole_opening_url}
      />

      {/* Phase 2.5 — Admin draft badge with Lock + Send Back actions */}
      <DraftBadge
        chain={lesson?.chain}
        onChainStatusChange={(newStatus) => {
          if (newStatus === 'locked' || newStatus === 'needs_revision') {
            setLesson((prev) => prev ? { ...prev, chain: { ...prev.chain, chain_status: newStatus } } : prev);
          }
        }}
      />

      <div ref={scrollRef} className="h-full overflow-y-auto" data-testid="content-scroll">
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-3 pb-16">
          <p
            className="font-mono text-[8px] italic leading-relaxed mb-3"
            style={{ color: era.textSecondary }}
            data-testid="voice-disclosure"
          >
            {lesson.voice_disclosure}
          </p>

          <StickyStepNav steps={STEPS} currentStep={currentStep} onStep={goStep} era={era} />

          <div className={`pt-4 ${frameClass}`}>
            <PictureShowHero
              images={stepImages}
              isPlaying={isPlaying}
              eraId={era.id}
              subject={lesson?.chain?.subject}
              stepLabel={step.label}
            />
          </div>

          <OnThisDayInline events={lesson.on_this_day || []} era={era} />

          <div className="my-4">
            <StepRibbon
              chain={lesson.chain}
              currentStepKey={step.key}
              onStepClick={(k) => {
                const idx = STEP_KEYS.indexOf(k);
                if (idx >= 0) goStep(idx);
              }}
            />
          </div>

          {narrationUrl && (
            <audio
              ref={audioRef}
              src={narrationUrl}
              preload="auto"
              onEnded={() => setIsPlaying(false)}
              data-testid="narration-audio"
            />
          )}

          <div className="flex items-center gap-3 my-4">
            <button
              data-testid="narration-play-pause"
              onClick={() => setIsPlaying(!isPlaying)}
              disabled={!narrationUrl}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full font-hud text-[10px] tracking-wider transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ border: `1px solid ${era.primary}33`, color: era.primary }}
            >
              {isPlaying ? <Pause size={12} /> : <Play size={12} />}
              {narrationUrl ? (isPlaying ? 'PAUSE' : 'PLAY') + ' NARRATION' : 'NARRATION UNAVAILABLE'}
            </button>
            <Volume2 size={12} className="text-[#52525B]" />
          </div>

          <div ref={scriptRef} className="pt-2" data-testid="lesson-script">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span
                className="font-hud text-[8px] tracking-widest uppercase px-2 py-0.5 rounded"
                style={{ color: era.primary, border: `1px solid ${era.primary}33`, background: `${era.primary}08` }}
                data-testid="era-badge"
              >
                {era.label}
              </span>
              {/* Fork button — only on locked / published chains, only for authed users */}
              {(lesson?.chain?.chain_status === 'locked' || lesson?.chain?.chain_status === 'published') && user && (
                <ForkChainButton chainId={lesson.chain.id} chainTitle={lesson.chain.subject} era={era} />
              )}
            </div>
            <h2
              className="text-xl md:text-2xl font-light tracking-tight mb-1"
              style={{ fontFamily: era.headingFont, color: era.accent }}
            >
              {lesson.chain.subject}
            </h2>
            <p className="font-hud text-[10px] tracking-widest uppercase mb-5" style={{ color: era.primary }}>
              {step.label} — {step.sublabel}
            </p>

            {isContradiction && !lesson.chain.has_contradiction ? (
              <div className="border border-[#D4A84A]/20 rounded p-5" data-testid="contradiction-empty">
                <AlertTriangle className="text-[#D4A84A] mb-3" size={20} />
                <p className="font-mono text-sm text-white/80 leading-relaxed italic">{EMPTY_CONTRADICTION}</p>
              </div>
            ) : isContradiction && lesson.chain.has_contradiction ? (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-4 era-script-transition"
                key={`contradiction-${step.key}`}
                data-testid="contradiction-split"
              >
                <ContradictionPanel
                  label="SOURCE A"
                  color="#2F8A92"
                  data={lesson.chain.contradiction}
                />
                <ContradictionPanel
                  label="SOURCE B"
                  color="#D4A84A"
                  data={lesson.chain.contradiction_source_b}
                />
              </div>
            ) : stepData ? (
              <div
                data-testid={`step-content-${step.key}`}
                key={`era-script-wrapper-${step.key}`}
                className="era-script-transition"
              >
                <EraScript
                  eraId={era.id}
                  title={stepData.title}
                  body={stepData.content}
                  animate={true}
                  soundEnabled={false}
                  meta={
                    <span className="flex flex-wrap items-center gap-3">
                      {stepData.source && (
                        <span className="inline-flex items-center gap-1">
                          <BookOpen size={9} /> {stepData.source}
                        </span>
                      )}
                      {stepData.year && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={9} /> {stepData.year < 0 ? `${Math.abs(stepData.year)} BCE` : `${stepData.year} CE`}
                        </span>
                      )}
                      {stepData.location && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={9} /> {stepData.location}
                        </span>
                      )}
                    </span>
                  }
                />
              </div>
            ) : (
              <p className="font-mono text-sm text-[#52525B]">No data for this step.</p>
            )}

            <MyRootsInlineCallout intersections={stepIntersections} era={era} />

            {/* ── Cross-references — parallel-time links per Foundation Brief §6 ──
                fromOrigin drives the great-circle arc transition that traces
                the spatial relationship between this chain and the target. */}
            <CrossReferenceBlock
              refs={(stepData?.cross_references) || []}
              era={era}
              onNavigate={(target) => onNavigate && onNavigate(target)}
              fromOrigin={resolveEraOrigin(chain)}
              fromLabel={chain?.title || ''}
            />
          </div>

          {/* ── Spine traversal — Foundation Brief §5 ── */}
          {(neighbors.previous || neighbors.next) && (
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="spine-traversal">
              {neighbors.previous ? (
                <button
                  data-testid="spine-prev-btn"
                  onClick={() => onNavigate && onNavigate({ id: neighbors.previous.id })}
                  className="text-left px-4 py-3 border border-[#2F8A92]/15 rounded hover:border-[#2F8A92]/40 transition-colors"
                >
                  <div className="font-hud text-[9px] md:text-[10px] tracking-widest uppercase text-[#2F8A92]">← Previous chain</div>
                  <div className="font-historical text-base md:text-lg text-[#e8d5a3] mt-1">{neighbors.previous.subject}</div>
                </button>
              ) : <div />}
              {neighbors.next ? (
                <button
                  data-testid="spine-next-btn"
                  onClick={() => onNavigate && onNavigate({ id: neighbors.next.id })}
                  className="text-right px-4 py-3 border border-[#D4A84A]/20 rounded hover:border-[#D4A84A]/45 transition-colors"
                >
                  <div className="font-hud text-[9px] md:text-[10px] tracking-widest uppercase text-[#D4A84A]">Continue the chain →</div>
                  <div className="font-historical text-base md:text-lg text-[#D4A84A] mt-1">{neighbors.next.subject}</div>
                </button>
              ) : <div />}
            </div>
          )}

          {/* ── Forward paths — no dead ends ── */}
          <div className="mt-10 pt-6 border-t border-[#2F8A92]/10" data-testid="forward-paths">
            <h4 className="font-hud text-[10px] text-white/80 tracking-widest uppercase mb-3">CONTINUE EXPLORING</h4>

            {/* Phase 3 — Teacher attribution surface (only shown if chain has any inspired_by entries) */}
            <InspiredBy chainId={lesson?.chain?.id} />

            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <button
                data-testid="prev-step-btn"
                onClick={() => goStep(currentStep - 1)}
                disabled={currentStep === 0}
                className="flex items-center gap-1 px-3 py-1.5 border border-[#2F8A92]/15 rounded font-hud text-[10px] tracking-wider text-[#2F8A92] hover:bg-[#2F8A92]/5 disabled:text-[#52525B] disabled:border-[#52525B]/15 transition-all duration-300"
              >
                <ChevronLeft size={12} /> PREVIOUS STEP
              </button>
              <span className="font-hud text-[9px] text-[#52525B]">
                {currentStep + 1} / {STEPS.length}
              </span>
              <button
                data-testid="next-step-btn"
                onClick={() => goStep(currentStep + 1)}
                disabled={currentStep === STEPS.length - 1}
                className="flex items-center gap-1 px-3 py-1.5 border border-[#2F8A92]/15 rounded font-hud text-[10px] tracking-wider text-[#2F8A92] hover:bg-[#2F8A92]/5 disabled:text-[#52525B] disabled:border-[#52525B]/15 transition-all duration-300"
              >
                NEXT STEP <ChevronRight size={12} />
              </button>
              <button
                data-testid="open-in-workcenter"
                onClick={() => onViewChange && onViewChange('workcenter')}
                className="flex items-center gap-2 px-3 py-1.5 border border-[#D4A84A]/25 rounded font-hud text-[10px] tracking-wider text-[#D4A84A] hover:bg-[#D4A84A]/5 transition-all duration-300 ml-auto"
              >
                <Pen size={12} /> OPEN IN WORK CENTER
              </button>
            </div>

            {lesson.related_chains?.length > 0 && (
              <div className="space-y-2">
                <p className="font-hud text-[9px] text-[#52525B] tracking-wider uppercase">RELATED CHAINS</p>
                {lesson.related_chains.map((rc) => (
                  <button
                    key={rc.id}
                    data-testid={`related-chain-${rc.id}`}
                    onClick={() => onNavigate(rc)}
                    className="w-full text-left px-3 py-2 border border-[#D4A84A]/10 rounded hover:border-[#D4A84A]/30 transition-all duration-300 group"
                  >
                    <span className="font-historical text-sm text-[#e8d5a3] group-hover:text-[#D4A84A] transition-all duration-300">{rc.subject}</span>
                    {rc.description && (
                      <p className="font-mono text-[8px] text-[#52525B] mt-0.5 truncate">{rc.description}</p>
                    )}
                  </button>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-[#2F8A92]/8 flex items-center gap-4 text-[10px] font-mono text-[#52525B]">
              <span className="inline-flex items-center gap-1">
                <Sparkles size={10} className="text-[#D4A84A]" /> {credits.current_balance} credits
              </span>
              <span>Lifetime earned: {credits.lifetime_earned}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Source-A / Source-B contradiction split panel — used only on the
 *  contradiction step when both sources are present. */
function ContradictionPanel({ label, color, data }) {
  if (!data) return null;
  return (
    <div className="border rounded p-4 border-l-2" style={{ borderColor: `${color}33`, borderLeftColor: color }}>
      <h4 className="font-hud text-[10px] tracking-wider uppercase mb-2" style={{ color }}>{label}</h4>
      <h3 className="font-historical text-[#D4A84A] text-base mb-2">{data.title}</h3>
      <p className="font-mono text-sm text-white/80 leading-relaxed">{data.content}</p>
      {data.source && (
        <p className="font-mono text-[9px] text-[#52525B] mt-2 flex items-center gap-1">
          <BookOpen size={10} /> {data.source}
        </p>
      )}
    </div>
  );
}
